<template>
    <!-- Row layout: small avatar left (remove on its corner), name/status beside
         it, and an optional status badge (Host/Joined) on the right -->
    <div
        v-if="horizontal"
        class="flex w-full min-w-0 items-center gap-3"
        data-testid="player-card"
        :data-player-name="player.tag_name"
        :data-player-status="status"
    >
        <div class="relative shrink-0">
            <Avatar
                :size="12"
                :src="player.avatar"
                :alt="player.tag_name"
                :border-color="player.ready ? 'border-stroke-1' : 'border-red-500'"
                :class="{ 'cursor-pointer': clickable }"
                @click="clickable ? $emit('click', player) : null"
            />
            <button
                v-if="showRemoveButton && player.profile_id !== currentUserId"
                data-testid="remove-player-button"
                @click="$emit('remove', player.profile_id)"
                :disabled="disabled"
                class="clip-diagonal-corners-small absolute -top-1.5 -left-1.5 flex size-4 items-center justify-center bg-red-500 text-xs font-bold hover:text-red-700 disabled:opacity-50"
            >
                ✕
            </button>
        </div>
        <div class="min-w-0 flex-1">
            <span
                class="line-clamp-1 overflow-hidden text-sm font-medium text-ellipsis text-white"
                >{{ player.tag_name }}</span
            >
            <div
                v-if="showStatus"
                class="mt-0.5 text-xs"
                :class="statusText ? 'text-titles' : player.ready ? 'text-green-500' : 'text-titles'"
            >
                {{ statusText || (player.ready ? $t('player_card.ready') : $t('player_card.pending')) }}
            </div>
        </div>

        <span
            v-if="badge"
            class="bg-zinc-900 shrink-0 px-3 py-1.5 text-xs font-bold uppercase border-zinc-700"
            :class="badgeClasses"
        >
            {{ badge }}
        </span>
    </div>

    <!-- Tile layout (default): full-width avatar with name/status underneath -->
    <div
        v-else
        class="relative flex w-full min-w-0 flex-col items-center justify-center gap-2"
        data-testid="player-card"
        :data-player-name="player.tag_name"
        :class="[centerItems ? 'items-center' : 'items-start']"
    >
        <Avatar
            size="w-full"
            :src="player.avatar"
            :alt="player.tag_name"
            :border-color="player.ready ? 'border-stroke-1' : 'border-red-500'"
            :class="{ 'cursor-pointer': clickable }"
            @click="clickable ? $emit('click', player) : null"
        />
        <div :class="{ 'mt-1': centerItems, 'text-center': centerItems }" class="w-full">
            <span
                class="line-clamp-1 overflow-hidden text-sm font-medium text-ellipsis text-white"
                >{{ player.tag_name }}</span>
            <div
                v-if="showStatus"
                class="mt-1 text-xs"
                :class="player.ready ? 'text-green-500' : 'text-titles'"
            >
                {{ player.ready ? $t('player_card.ready') : $t('player_card.pending') }}
            </div>
        </div>

        <button
            v-if="showRemoveButton && player.profile_id !== currentUserId"
            data-testid="remove-player-button"
            @click="$emit('remove', player.profile_id)"
            :disabled="disabled"
            :class="[
                'clip-diagonal-corners-small absolute bg-red-500 font-bold hover:text-red-700 disabled:opacity-50',
                compact
                    ? '-top-[3px] -right-[3px] size-4 text-xs'
                    : '-top-[8px] -right-[8px] size-6',
            ]"
        >
            ✕
        </button>
    </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue'

export default {
    name: 'PlayerCard',
    components: { Avatar },
    props: {
        player: {
            type: Object,
            required: true,
        },
        currentUserId: {
            type: [String, Number],
            default: null,
        },
        showStatus: {
            type: Boolean,
            default: true,
        },
        showRemoveButton: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clickable: {
            type: Boolean,
            default: false,
        },
        imageTitle: {
            type: String,
            default: '',
        },
        compact: {
            type: Boolean,
            default: false,
        },
        centerItems: {
            type: Boolean,
            default: false,
        },
        /** Render as a horizontal row (small avatar + name) instead of a tile. */
        horizontal: {
            type: Boolean,
            default: false,
        },
        /** Status badge shown at the right of a horizontal row (e.g. Host / Joined). */
        badge: {
            type: String,
            default: '',
        },
        /** Badge accent: 'success' (green, in the game) or 'neutral' (pending). */
        badgeVariant: {
            type: String,
            default: 'neutral',
        },
        /** Overrides the Ready/Pending status line (e.g. "Bot"). */
        statusText: {
            type: String,
            default: '',
        },
        /** Machine-readable status for tests ('host' | 'joined' | 'invited'), independent of translated badge text. */
        status: {
            type: String,
            default: '',
        },
    },
    emits: ['remove', 'click'],
    computed: {
        badgeClasses() {
            return this.badgeVariant === 'success'
                ? 'text-green-500'
                : 'text-orange-500'
        },
    },
}
</script>
